import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

export interface ISRWorkspaceDrawerControls {
  requestClose: () => void;
  expanded: boolean;
}

export interface ISRWorkspaceDrawerShellProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode | ((controls: ISRWorkspaceDrawerControls) => React.ReactNode);
  /**
   * Lead identity/overview content rendered in the dimmed area LEFT of the
   * drawer. Only shown on wide screens (xl+) — it reuses otherwise-empty
   * backdrop space instead of drawer real estate.
   */
  contextPanel?: React.ReactNode;
  /** Pinned bottom strip for the left panel — sits at the same level as the
      right panel's footer (clock/weather/news). */
  contextFooter?: React.ReactNode;
  /** Navigate between leads without closing the drawer. */
  onPreviousLead?: () => void;
  onNextLead?: () => void;
  hasPreviousLead?: boolean;
  hasNextLead?: boolean;
  /** 1-based position in the list, shown between the arrows (e.g. 3/24). */
  leadPosition?: number;
  totalLeads?: number;
  hasUnsavedChanges?: boolean;
  confirmDiscard?: () => boolean;
  titleId?: string;
  label?: string;
  className?: string;
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible, reusable drawer shell for the ISR workspace.
 *
 * Rendered through a portal to document.body so it can never be trapped in an
 * ancestor stacking context (the platform header is fixed at z-50 inside the
 * app root; a non-portaled fixed drawer can end up painted under it).
 *
 * It owns modal focus, Escape, focus return, page scroll locking and the
 * unsaved-changes guard. Its render prop passes the guarded close action to
 * content rendered inside the drawer.
 */
export const ISRWorkspaceDrawerShell: React.FC<ISRWorkspaceDrawerShellProps> = ({
  open,
  onClose,
  children,
  contextPanel,
  contextFooter,
  onPreviousLead,
  onNextLead,
  hasPreviousLead = false,
  hasNextLead = false,
  leadPosition,
  totalLeads = 0,
  hasUnsavedChanges = false,
  confirmDiscard,
  titleId = 'isr-workspace-title',
  label = 'SDR workspace',
  className = '',
}) => {
  const drawerRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const requestCloseRef = useRef<() => void>(() => undefined);

  const requestClose = useCallback(() => {
    if (hasUnsavedChanges) {
      const shouldDiscard = confirmDiscard
        ? confirmDiscard()
        : window.confirm('Discard your unsaved SDR changes?');
      if (!shouldDiscard) return;
    }
    onClose();
  }, [confirmDiscard, hasUnsavedChanges, onClose]);

  // Keep the keyboard listener stable while the form becomes dirty. Rebinding
  // the modal effect on every edit would steal focus back to the first control.
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusDrawer = window.requestAnimationFrame(() => {
      const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(focusableSelector);
      (firstFocusable || drawerRef.current)?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        drawerRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusDrawer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000]">
      {/* Backdrop — click closes. Heavier blur + dim so the identity panel
          reads clearly against the page behind it. */}
      <button
        type="button"
        aria-label="Close SDR workspace"
        onClick={requestClose}
        className="absolute inset-0 cursor-default bg-[#020306]/90 backdrop-blur-[16px] transition-opacity duration-200 motion-reduce:transition-none"
      />

      <div className="absolute inset-0 flex justify-end">
        {/* Lead identity panel in the dimmed left area (wide screens only).
            The bottom strip sits at the same vertical level as the right
            panel's footer — fixed at the bottom of this column. */}
        {contextPanel ? (
          <div className="pointer-events-none hidden min-w-0 flex-col overflow-hidden px-6 pb-1.5 pt-6 2xl:px-8 2xl:pb-2 2xl:pt-7 xl:flex xl:w-[30%] xl:flex-none">
            <div className="pointer-events-auto min-h-0 flex-1 overflow-hidden">
              {contextPanel}
            </div>
            {contextFooter}
          </div>
        ) : null}

        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-label={titleId ? undefined : label}
          tabIndex={-1}
          className={`relative flex h-full w-full max-w-full shrink-0 flex-col overflow-hidden border-l border-white/8 bg-[#0a0b0e] shadow-[-24px_0_80px_rgba(0,0,0,0.55)] outline-none sm:w-[clamp(720px,58vw,1040px)] xl:w-[70%] xl:max-w-none ${className}`}
        >
          
          <div className="min-h-0 flex-1 [overscroll-behavior:contain]">
            {typeof children === 'function' ? children({ requestClose, expanded: false }) : children}
          </div>
        </aside>
      </div>
    </div>,
    document.body,
  );
};

export default ISRWorkspaceDrawerShell;
